import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Api } from '@.api';
import { CreateCompaniesType, DictionariesResponseType } from '@.api/methods';
import { useForm } from '@.hooks';
import { Layout } from '@.layout';
import { LinkHelper } from '@.services';
import { Input, PhoneInput, Select } from '@.ui';
import { Buttons } from '@/Components/Partials';
import { ParentCompanyPreviewResource } from '@/Types/ParentCompany';

import { CompaniesActionProps, CompaniesEntityScheme } from './types';
import { validateOptions } from './validate';

export const CompaniesAction: React.FC<CompaniesActionProps> = ({ edit }) => {
    const [statuses, setStatuses] = useState<DictionariesResponseType>();

    const [parentCompanies, setParentCompanies] = useState<ParentCompanyPreviewResource[]>([]);

    const [field, form] = useForm<CompaniesEntityScheme>({});

    const navigate = useNavigate();

    const { id } = useParams();

    const breadcrumbs = edit
        ? [
              { name: 'Companies', link: LinkHelper.clientsCompanies },
              { name: form.state.name?.value ?? '', link: LinkHelper.clientsCompanyId(Number(id)) },
              { name: 'Edit', current: true },
          ]
        : [
              { name: 'Companies', link: LinkHelper.clientsCompanies },
              { name: 'Add', current: true },
          ];

    const formTitle = edit ? <h1>Editing company</h1> : <h1>Adding company</h1>;

    const serializeDataToRequest = (data: CompaniesEntityScheme): CreateCompaniesType => {
        const dataRequest: CreateCompaniesType = {
            ...omit(data, ['latitude', 'longitude', 'status_id']),
        };

        if (data.latitude && data.longitude) {
            dataRequest.gps_coords = {
                lat: data.latitude,
                lng: data.longitude,
            };
        }

        if (data.status_id) {
            dataRequest.status = data.status_id;
        }

        return dataRequest;
    };

    const createValidateOptions = () => {
        const { agent_phone, latitude, longitude } = form.state;

        return validateOptions(agent_phone?.value, Boolean(latitude?.value || longitude?.value));
    };

    const handleCreate = () => {
        form.reset.errors();

        form.ifValid((data) => {
            Api.companies()
                .create(serializeDataToRequest(data))
                .onSuccess(() => navigate(LinkHelper.clientsCompanies))
                .onValidationError((res) => form.set.errors(res.errors));
        }, createValidateOptions());
    };

    const handleUpdate = () => {
        form.reset.errors();

        form.ifValid((data) => {
            Api.companies()
                .update(serializeDataToRequest(data), Number(id))
                .onSuccess(() => navigate(LinkHelper.clientsCompanies))
                .onValidationError((res) => form.set.errors(res.errors));
        }, createValidateOptions());
    };

    useEffect(() => {
        if (id) {
            Api.companies()
                .getById(Number(id))
                .onSuccess((res) => {
                    const data = res.data;

                    form.set.values({
                        ...data,
                        name: data.name,
                        agent_email: data.agent_email,
                        agent_name: data.agent_name,
                        agent_phone: data.agent_phone,
                        status_id: data.status?.id || null,
                        latitude: data.gps_coords?.lat,
                        longitude: data.gps_coords?.lng,
                        parent_company_id: data.parent_company?.id,
                    });
                });
        }

        if (edit) {
            Api.dictionaries()
                .get({ by_name: 'company_statuses' })
                .onSuccess((res) => setStatuses(res.data));
        }

        Api.companies()
            .formSupport()
            .onSuccess((res) => setParentCompanies(res.data.parent_companies));
    }, []);

    return (
        <Layout name="clients" breadcrumbs={breadcrumbs} small>
            {formTitle}
            <div className="companies-action__form">
                <Input {...field('name').register()} text="Name" required />

                <Input {...field('country').register()} text="Country" required />

                <Input {...field('street_address_1').register()} text="Street address 1" required />

                <Input {...field('street_address_2').register()} text="Street address 2" />

                <Input {...field('region').register()} text="State/Province/Region" required />

                <Input {...field('postal').register()} text="Zip/Postal Code" required />

                <Input {...field('city').register()} text="City" required />

                <Select {...field('parent_company_id').register<number>()} label="Parent company" placeholder="Parrent company" theme="white" required>
                    {parentCompanies.map((parentCompany) => (
                        <Select.Option key={parentCompany.id} id={parentCompany.id}>
                            {parentCompany.name}
                        </Select.Option>
                    ))}
                </Select>

                <div className="companies-action__form_gps">
                    <h3>GPS Coordinates (Lat, Long)</h3>

                    <div className="companies-action__form_gps_coords">
                        <Input {...field('latitude').register()} text="Latitude" type="number" />

                        <Input {...field('longitude').register()} text="Longitude" type="number" />
                    </div>
                </div>

                <Input {...field('agent_name').register()} text="Agent name" required />

                <PhoneInput {...field('agent_phone').register()} label="Phone" required />

                <Input {...field('agent_email').register()} text="Email" required />

                {edit && (
                    <Select {...field('status_id').register<number>()} placeholder="Status" theme="white" required>
                        {statuses?.company_statuses?.map((status) => (
                            <Select.Option id={status.id} key={status.id}>
                                {status.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}

                <Buttons text={edit ? 'Save' : 'Add'} onClickSave={edit ? handleUpdate : handleCreate} />
            </div>
        </Layout>
    );
};
